import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../hooks/useAuth'

// Signs the user out after 8 hours with no activity (decided Oct 2026), with a 5-minute warning first.
// Activity is shared across tabs through localStorage, so working in one tab keeps the others signed in.
// Coming back to a tab or laptop after more than 8 hours signs out straight away.
const IDLE_MS = 8 * 60 * 60 * 1000
const WARN_MS = 5 * 60 * 1000
const KEY = 'inv_last_activity'
export const IDLE_FLAG = 'inv_signed_out_idle' // read by the Login page to explain why

const readLast = () => { try { return Number(localStorage.getItem(KEY)) || 0 } catch (e) { return 0 } }
const writeLast = t => { try { localStorage.setItem(KEY, String(t)) } catch (e) { /* storage blocked: this tab still tracks in memory */ } }

export default function IdleSignOut() {
  const { signOut } = useAuth()
  const [warning, setWarning] = useState(false)
  const last = useRef(Date.now())
  const signingOut = useRef(false)

  useEffect(() => {
    // First run after sign-in (or after this feature shipped): start the clock now
    const stored = readLast()
    if (stored && Date.now() - stored >= IDLE_MS) { doSignOut(); return }
    last.current = Date.now()
    writeLast(last.current)

    let lastWrite = 0
    const onActivity = () => {
      const now = Date.now()
      last.current = now
      setWarning(false)
      if (now - lastWrite > 30000) { lastWrite = now; writeLast(now) } // throttle storage writes
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }))

    const check = () => {
      const latest = Math.max(last.current, readLast())
      const idle = Date.now() - latest
      if (idle >= IDLE_MS) doSignOut()
      else setWarning(idle >= IDLE_MS - WARN_MS)
    }
    const timer = setInterval(check, 30000)
    const onVisible = () => { if (document.visibilityState === 'visible') check() }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity))
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line
  }, [])

  async function doSignOut() {
    if (signingOut.current) return
    signingOut.current = true
    try { sessionStorage.setItem(IDLE_FLAG, '1') } catch (e) { /* ignore */ }
    try { localStorage.removeItem(KEY) } catch (e) { /* ignore */ }
    await signOut()
    window.location.assign('/login')
  }

  if (!warning) return null
  return (
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000, maxWidth: '360px', background: 'var(--bg-3)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)', padding: '14px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', fontSize: '13px' }}>
      <div style={{ fontWeight: 600, marginBottom: '6px' }}>Still there?</div>
      <div style={{ color: 'var(--text-2)', marginBottom: '10px' }}>You'll be signed out in a few minutes because of inactivity.</div>
      <button className="btn btn-primary btn-sm" onClick={() => { last.current = Date.now(); writeLast(last.current); setWarning(false) }}>Stay signed in</button>
    </div>
  )
}
